import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { captureSilentError } from '../services/posthog';
import { useDataStore } from '../stores/useDataStore';
import { useUIStore } from '../stores/useUIStore';
import { AddModal } from './AddModal';
import { AdminPanel } from './AdminPanel';
import { FilterModal } from './FilterModal';
import { InstallHelpModal } from './InstallHelpModal';
import { InstallPrompt } from './InstallPrompt';
import { NotificationsModal } from './NotificationsModal';
import { ProfileModal } from './ProfileModal';
import { SupportModal } from './SupportModal';
import { WelcomeModal } from './WelcomeModal';

interface GlobalModalsProps {
    // Notifications (Still passed from App level hook)
    permissionStatus: NotificationPermission;
    onRequestNotificationPermission: () => Promise<void>;
}

export const GlobalModals: React.FC<GlobalModalsProps> = ({
    permissionStatus,
    onRequestNotificationPermission
}) => {
    // Stores
    const { 
        isAddModalOpen, closeAddModal,
        isProfileOpen, closeProfile, openProfile,
        isSupportModalOpen, closeSupport, openSupport,
        isAdminPanelOpen, closeAdmin, openAdmin,
        isFilterOpen, closeFilter,
        isWelcomeModalOpen, closeWelcome, openWelcome,
        isInstallHelpOpen, closeInstallHelp, openInstallHelp,
        isNotificationsOpen, closeNotifications,
        
        filters, setFilters,
        openAddModal,
        setViewMode
    } = useUIStore();

    const { 
        addRecommendation,
        shouts
    } = useDataStore();

    const { currentUser } = useAuth();

    // Derived State
    const myShoutCount = currentUser ? shouts.filter(s => s.userId === currentUser.uid).length : 0;

    // Handlers
    const handleAddRecommendation = async (data: any) => {
        if (!currentUser) return;
        // Note: Logic for setting selectedVenueId moved to App or creating a dedicated flow?
        // In App.tsx handleAddRecommendation did: add -> replaceHistory -> closeAdd -> selectVenue -> pan.
        // We can replicate that logic here or in the store action.
        // But addRecommendation needs to return the new ID.
        // Let's keep the logic simple here for now.
        
        try {
           const newVenueId = await addRecommendation(currentUser, data);
           if (newVenueId) {
             // We need to select the venue and maybe change view mode
             useUIStore.getState().transitionAddModalToVenue(newVenueId);
             
             // Panning is handled by Map component reacting to selectedVenueId? Or needs manual pan?
             // App.tsx did manual pan. We don't have mapInstance here. 'selectVenue' implies focus.
             // We can let the Map component listen to selectedVenueId change and pan?
             // Or rely on user to find it.
             // Ideally Map pans to selected venue. 
             // We'll address map panning in the Map component.
           }
        } catch (e) {
            captureSilentError('GlobalModals.handleAddRecommendation', e);
            throw e;
        }
    };

    const handleFriendClick = (friendId: string) => {
        closeProfile();
        // Reset other filters to be clean?
        setFilters({
            categories: [],
            statuses: [],
            userIds: [friendId]
        });
        setViewMode('list');
    };

    const handleWelcomeAddFirst = () => {
        closeWelcome();
        openAddModal();
    };

    return (
        <>
            <AddModal 
                isOpen={isAddModalOpen} 
                onClose={closeAddModal} 
                onSubmit={handleAddRecommendation}
            />

            <ProfileModal
                isOpen={isProfileOpen}
                onClose={closeProfile}
                shoutCount={myShoutCount}
                onInstallApp={openInstallHelp}
                onOpenSupport={openSupport}
                onOpenAdmin={openAdmin}
                onFriendClick={handleFriendClick}
                notificationPermission={permissionStatus}
                onRequestNotificationPermission={onRequestNotificationPermission}
            />
            
            <SupportModal 
                isOpen={isSupportModalOpen}
                onClose={closeSupport}
            />

            <AdminPanel 
                isOpen={isAdminPanelOpen}
                onClose={closeAdmin}
            />
            
            <FilterModal 
                isOpen={isFilterOpen}
                onClose={closeFilter}
                filters={filters}
                setFilters={setFilters}
            />

            <WelcomeModal 
                isOpen={isWelcomeModalOpen}
                onClose={closeWelcome}
                onAddFirst={handleWelcomeAddFirst}
            />

            <InstallHelpModal
                isOpen={isInstallHelpOpen}
                onClose={closeInstallHelp}
            />

            <InstallPrompt 
                isWelcomeModalOpen={isWelcomeModalOpen}
                onOpenHelp={openInstallHelp}
            />

            <NotificationsModal
                isOpen={isNotificationsOpen}
                onClose={closeNotifications}
            />
        </>
    );
};
